import { useTheme } from '@mui/styles';
import {
  U21Divider,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { IconFlag, IconFlagCheck, IconFlagX } from '@u21/tabler-icons';
import { Condition } from 'app/modules/detectionModels/components/ConditionBreakdown/Condition';
import styled from 'styled-components';
import { EnrichedQueryTreeNode } from 'app/modules/rules/types/responses';

interface OperandProps {
  op: EnrichedQueryTreeNode;
  group: string;
  isFirst: boolean;
}

interface EvaluationFlagProps {
  evaluationResult: boolean | null;
}

export const EvaluationFlag = ({ evaluationResult }: EvaluationFlagProps) => {
  const { palette } = useTheme();
  if (evaluationResult === null) {
    return <IconFlag size="24" color={palette.text.secondary} />;
  }

  return evaluationResult ? (
    <IconFlagCheck size="24" color={palette.success.main} />
  ) : (
    <IconFlagX size="24" color={palette.error.main} />
  );
};

export const Operand = ({ op, group, isFirst = false }: OperandProps) => {
  if (
    (op?.type === 'COMPOSITE' || op?.type === 'rule_condition_composite') &&
    ['AND'].includes(op.operator)
  ) {
    return (
      <U21Spacer>
        {!isFirst && (
          <PaddedU21Typography variant="body2" color="text.secondary">
            {group}
          </PaddedU21Typography>
        )}
        <U21Spacer horizontal spacing={2}>
          <EvaluationFlag evaluationResult={op.evaluation_result} />
          <PaddedU21Typography
            variant="body2"
            color="text.secondary"
            component="div"
          >
            All of the following conditions was met:
          </PaddedU21Typography>
        </U21Spacer>
        <StyledSpacer horizontal spacing={4}>
          <StyledU21Divider $condition={op.evaluation_result} flexItem />
          <div>
            {op.operands.map((child, index) => (
              <Operand
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                op={child}
                group={op.operator}
                isFirst={index === 0}
              />
            ))}
          </div>
        </StyledSpacer>
      </U21Spacer>
    );
  }

  if (
    (op?.type === 'COMPOSITE' || op?.type === 'rule_condition_composite') &&
    ['OR'].includes(op.operator)
  ) {
    return (
      <U21Spacer>
        {!isFirst ? (
          <PaddedU21Typography variant="body2" color="text.secondary">
            {group}
          </PaddedU21Typography>
        ) : (
          <div />
        )}
        <U21Spacer horizontal spacing={2}>
          <EvaluationFlag evaluationResult={op.evaluation_result} />
          <PaddedU21Typography
            variant="body2"
            color="text.secondary"
            component="div"
          >
            One of the following conditions was met:
          </PaddedU21Typography>
        </U21Spacer>
        <StyledSpacer horizontal spacing={4}>
          <StyledU21Divider $condition={op.evaluation_result} />
          <div>
            {op.operands.map((child, index) => (
              <Operand
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                op={child}
                group={op.operator}
                isFirst={index === 0}
              />
            ))}
          </div>
        </StyledSpacer>
      </U21Spacer>
    );
  }

  return (
    <>
      {!isFirst ? (
        <PaddedU21Typography variant="body2" color="text.secondary">
          {group}
        </PaddedU21Typography>
      ) : (
        <div />
      )}
      <Condition op={op} />
    </>
  );
};

// ignore cartilage, for my own benefit - will remove after skeleton is built on
const StyledU21Divider = styled(U21Divider)<{ $condition?: boolean | null }>`
  ${(props) =>
    props.$condition !== null
      ? `
          color: ${
            props.$condition
              ? props.theme.palette.success.main
              : props.theme.palette.error.main
          };
        `
      : ''}
  }};
`;

const PaddedU21Typography = styled(U21Typography)`
  padding-left: 2px;
  padding-top: 4px;
  padding-bottom: 4px;
`;

const StyledSpacer = styled(U21Spacer)`
  padding-left: 10px;
`;
